import { Ref, ref } from '@vue/composition-api';
import _ from 'lodash';

// 引入 compositions
import openPreviewHandler from '@/renderer/components/SpacePage/compositions/openPreviewHandler';

// 引入工具类
import smh from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import tips from '@/renderer/scripts/utils/tips';
import router from '@/renderer/router';
import store from '@/renderer/store';
import i18n, { i18n_t } from '@/locales/i18n';
import { loopAsyncTask } from '@/renderer/scripts/utils/syncTask';
import { ListItem } from '@/renderer/components/dialog/SelectFilePath/compositions/stencilHandler';
type Handler = {
  showCreateFolder: Ref<boolean>;
  showCreateDocument: Ref<boolean>;
  showCreateStencil: Ref<boolean>;
  createDocumentType: Ref<string>;
  methods: {
    openCreateFolder: () => void;
    openCreateDocument: (type: string) => void;
    openCreateStencil: () => void;
    onCreateFolder: (
      name: string,
      currPath: string[],
      hideTips?: boolean,
      conflictResolutionStrategy?: 'rename' | 'ask',
    ) => Promise<void>;
    onCreateDocument: (params: any, currPath: string[]) => void;
    onCreateStencil: (params: any, currPath: string[]) => void;
  };
};
export default function handler(spaceId: Ref<SpaceId>): Handler {
  const showCreateFolder = ref(false);
  const showCreateDocument = ref(false);
  // 创建模板
  const showCreateStencil = ref(false);
  const createDocumentType = ref('');
  const { methods: previewMethods } = openPreviewHandler(spaceId);
  const methods = {
    openCreateFolder() {
      showCreateFolder.value = true;
    },
    openCreateDocument(type: string) {
      createDocumentType.value = type;
      showCreateDocument.value = true;
    },
    openCreateStencil() {
      showCreateStencil.value = true;
    },
    async onCreateFolder(
      name: string,
      currPath: string[],
      hideTips?: boolean,
      conflictResolutionStrategy?: 'rename' | 'ask',
    ) {
      const fullPath = [...currPath];
      fullPath.push(name); // 计算文件全路径

      const folderName = fullPath.join('/');
      try {
        const { data } = await smh.commonAPI.createDirectory({
          dirPath: folderName,
          conflictResolutionStrategy: conflictResolutionStrategy || 'rename',
          spaceId: spaceId.value,
          collectToken: store.getters.collectToken,
        });
        const resData = [
          {
            id: _.uniqueId('newFolder'),
            name: data.path[data.path.length - 1],
            type: 'dir',
            creationTime: data.creationTime,
            modificationTime: data.creationTime,
            path: data.path,
            new: true,
            favorited: false,
            userId: store.getters.userId,
          },
        ];

        showCreateFolder.value = false;
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH, resData, 'updateAuth');
        reporter.report('newfolder_finished', {
          file_name: name,
        });
        if (!hideTips) {
          tips.success(i18n_t('新建成功'));
        }
      } catch (error) {
        if (!hideTips) {
          tips.error(error as MsgError);
        }
      }
    },
    async onCreateDocument(params: any, currPath: string[]) {
      const { name, fromTemplate, createMode } = params;
      const fullPath = [...currPath];
      fullPath.push(name); // 计算文件全路径
      const docFilePath = fullPath.join('/');
      try {
        const { data } = await smh.commonAPI.createDoc({
          fromTemplate,
          filePath: docFilePath,
          spaceId: spaceId.value,
        });
        showCreateDocument.value = false;
        let eventCode = '';
        if (fromTemplate.endsWith('.docx')) {
          eventCode = 'new_word_finished';
        } else if (fromTemplate.endsWith('.xlsx')) {
          eventCode = 'new_excel_finished';
        } else if (fromTemplate.endsWith('.pptx')) {
          eventCode = 'new_ppt_finished';
        }
        reporter.report(eventCode, {
          file_name: name,
        });
        tips.success(i18n_t('创建成功'));
        (data as any).id = _.uniqueId('newDocument');
        (data as any).spaceOrgId = router.currentRoute?.query?.spaceOrgId || store.getters.orgId;
        ipc.emit(
          ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH,
          [
            {
              ...data,
              previewByDoc: true,
              new: true,
              favorited: false,
              userId: store.getters.userId,
            },
          ],
          'updateAuth',
        );

        if (createMode === 'createAndEdit') {
          previewMethods.openEdit(data);
        }
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    async onCreateStencil(params: ListItem[], currPath: string[]) {
      const fullPath = [...currPath];
      const stencilFilePath = fullPath.join('/');
      try {
        tips.loading(i18n_t('模板文件导入中'), 0);
        const res = await smh.commonAPI.createStencilFile({
          templateIds: params.map(item => item.id),
          filePath: stencilFilePath,
          spaceId: spaceId.value,
        });
        // 异步导入过程
        if (res.status === 200) {
          ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
          showCreateStencil.value = false;
          tips.success(i18n_t('模板文件导入成功'));
          reporter.report('new_template_finished', {
            template_name: params.map(item => item.templateName).join(','),
          });
        } else if (res.status === 202) {
          const { taskId } = res.data;
          const asyncRes = await loopAsyncTask(smh.commonAPI.asyncTaskQuery, taskId as number, spaceId.value);
          if (asyncRes.status === 200) {
            ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
            showCreateStencil.value = false;
            tips.success(i18n_t('模板文件导入成功'));
            reporter.report('new_template_finished', {
              template_name: params.map(item => item.templateName).join(','),
            });
          } else if (asyncRes.result) {
            // 后端统一处理错误code
            tips.error(asyncRes.result.code as MsgError);
            showCreateStencil.value = false;
          }
        }
      } catch (error) {
        showCreateStencil.value = false;
        tips.error(error as MsgError);
      }
    },
  };
  return {
    showCreateFolder,
    showCreateDocument,
    showCreateStencil,
    createDocumentType,
    methods,
  };
}
